import * as tslib_1 from "tslib";
/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { Text } from "./Text";
import PropTypes from "prop-types";
import { RequestCloseContext } from "./Sheet";
import { useTheme } from "./Theme/Providers";
import { noOp } from "./misc/noop";
import { useTouchable } from "touchable-hook";
import cx from "classnames";
import { safeBind } from "./Hooks/compose-bind";
var KeyCodes = {
    ArrowUp: 38,
    ArrowDown: 40,
    Home: 36,
    End: 35
};
var MenuListContext = React.createContext({
    focus: false,
    onKeyDown: function () { }
});
export var MenuList = function (_a) {
    var children = _a.children, _b = _a.focusableChildren, focusableChildren = _b === void 0 ? [] : _b, other = tslib_1.__rest(_a, ["children", "focusableChildren"]);
    var theme = useTheme();
    var disabled = new Map();
    var _c = React.useState(null), focusIndex = _c[0], setFocusIndex = _c[1];
    var kids = React.Children.toArray(children);
    var focusComponents = [MenuItem].concat(focusableChildren);
    var focusableItems = kids.filter(function (kid) {
        return React.isValidElement(kid) &&
            focusComponents.indexOf(kid.type) > -1;
    });
    var lastIndex = focusableItems.length - 1;
    var firstIndex = 0;
    return (jsx("div", tslib_1.__assign({ role: "menu", onKeyDown: function (e) {
            if (e.keyCode === KeyCodes.ArrowDown ||
                (e.keyCode === KeyCodes.ArrowUp && focusIndex === null)) {
                e.preventDefault();
                setFocusIndex(0);
            }
        }, css: {
            minWidth: "200px",
            display: "block",
            padding: theme.spaces.sm + " 0"
        } }, other), kids.map(function (kid) {
        if (!React.isValidElement(kid)) {
            return null;
        }
        var i = focusableItems.indexOf(kid);
        if (i < 0) {
            return kid;
        }
        disabled.set(i, kid.props.disabled);
        function focusDown(current) {
            var next = current + 1 > lastIndex ? firstIndex : current + 1;
            if (disabled.get(next)) {
                focusDown(next);
            }
            else {
                setFocusIndex(next);
            }
        }
        function focusUp(current) {
            var next = current - 1 < firstIndex ? lastIndex : current - 1;
            if (disabled.get(next)) {
                focusUp(next);
            }
            else {
                setFocusIndex(next);
            }
        }
        var index = focusIndex || 0;
        return (jsx(MenuListContext.Provider, { key: i, value: {
                focus: i === focusIndex,
                onKeyDown: function (e) {
                    if (e.keyCode === KeyCodes.ArrowDown) {
                        e.preventDefault();
                        focusDown(index);
                    }
                    else if (e.keyCode === KeyCodes.ArrowUp) {
                        e.preventDefault();
                        focusUp(index);
                    }
                    else if (e.keyCode === KeyCodes.Home) {
                        e.preventDefault();
                        setFocusIndex(firstIndex);
                    }
                    else if (e.keyCode === KeyCodes.End) {
                        e.preventDefault();
                        setFocusIndex(lastIndex);
                    }
                }
            } }, kid));
    })));
};
MenuList.propTypes = {
    children: PropTypes.node,
    focusableChildren: PropTypes.arrayOf(PropTypes.elementType)
};
export var MenuItem = function (_a) {
    var _b;
    var contentBefore = _a.contentBefore, contentAfter = _a.contentAfter, _c = _a.onPress, onPress = _c === void 0 ? noOp : _c, _d = _a.className, className = _d === void 0 ? "" : _d, _e = _a.component, Component = _e === void 0 ? "div" : _e, _f = _a.role, role = _f === void 0 ? "menuitem" : _f, children = _a.children, disabled = _a.disabled, other = tslib_1.__rest(_a, ["contentBefore", "contentAfter", "onPress", "className", "component", "role", "children", "disabled"]);
    var theme = useTheme();
    var dark = theme.colors.mode === "dark";
    var localRef = React.useRef(null);
    var closeParent = React.useContext(RequestCloseContext);
    var _g = React.useContext(MenuListContext), focus = _g.focus, onKeyDown = _g.onKeyDown;
    var isLink = Component === "a" || other.href || other.to;
    var _h = useTouchable({
        onPress: select,
        disabled: disabled,
        delay: 0,
        behavior: isLink ? "link" : "button"
    }), bind = _h.bind, hover = _h.hover, active = _h.active;
    React.useEffect(function () {
        if (focus && localRef.current) {
            localRef.current.focus();
        }
    }, [focus, localRef]);
    function select() {
        onPress();
        closeParent();
    }
    return (jsx(Component, tslib_1.__assign({ className: cx("MenuItem", "Touchable", className, {
            "Touchable--hover": hover,
            "Touchable--active": active
        }), css: [
            (_b = {
                    cursor: "pointer",
                    padding: "calc(" + theme.spaces.sm + " + 0.25rem) calc(" + theme.spaces.md + " + 0.25rem)"
                },
                _b[theme.mediaQueries.sm] = {
                    padding: theme.spaces.sm + " " + theme.spaces.md
                },
                _b.opacity = disabled ? 0.3 : 1,
                _b.display = "flex",
                _b.textDecoration = "none",
                _b.transition = "background-color 0.1s ease",
                _b.WebkitTapHighlightColor = "transparent",
                _b.color = theme.colors.text.default,
                _b.alignItems = "center",
                _b[":focus"] = {
                    outline: theme.outline
                },
                _b[":focus:not([data-focus-visible-added])"] = {
                    outline: "none"
                },
                _b[theme.mediaQueries.md] = {
                    padding: theme.spaces.xs + " " + theme.spaces.md
                },
                _b),
            hover && {
                background: dark
                    ? theme.colors.background.tint2
                    : theme.colors.background.tint1
            },
            active && {
                background: theme.colors.background.tint2
            }
        ], role: role, tabIndex: disabled ? -1 : 0, "data-trigger-close": true }, safeBind(bind, {
        ref: localRef,
        onKeyDown: function (e) {
            e.stopPropagation();
            if (onKeyDown)
                onKeyDown(e);
        }
    }, other)),
        contentBefore,
        typeof children === "string" ? (jsx(Text, { wrap: false, css: {
                paddingLeft: contentBefore ? theme.spaces.md : 0,
                paddingRight: contentAfter ? theme.spaces.md : 0,
                flex: 1,
                color: "inherit"
            } }, children)) : (children),
        typeof contentAfter === "string" ? (jsx(Text, { muted: true }, contentAfter)) : (contentAfter)));
};
MenuItem.propTypes = {
    onSelect: PropTypes.func,
    component: PropTypes.string,
    disabled: PropTypes.bool,
    children: PropTypes.node,
    contentBefore: PropTypes.node,
    contentAfter: PropTypes.node,
    onPress: PropTypes.func,
    className: PropTypes.string,
    role: PropTypes.string
};
export function MenuDivider(props) {
    var theme = useTheme();
    return (jsx("div", tslib_1.__assign({ css: {
            height: 0,
            margin: theme.spaces.sm + " 0",
            overflow: "hidden",
            borderTop: "1px solid",
            borderColor: theme.colors.border.muted
        } }, props)));
}
export var MenuLabel = function (props) {
    var theme = useTheme();
    return (jsx(Text, tslib_1.__assign({ variant: "uppercase", css: {
            padding: theme.spaces.xs + " " + theme.spaces.md
        } }, props)));
};
MenuLabel.propTypes = {
    children: PropTypes.node
};
